import { liteClient } from 'algoliasearch/lite';
import { ClientOptions } from '@algolia/client-common';
import { ProductClient } from '@jbhi-fi/apis/src/algolia/ProductClient';
import { baseConfig } from '../../config/algolia';

/**
 * Creates a proxy around an Algolia search client that filters out
 * individual empty requests from each batch.
 *
 * This proxy filters out requests that have no search intent
 *  1. Filters out requests that have neither a query nor filters
 *  2. Sends only requests with search intent to Algolia
 *  3. Stitches stub responses back into the correct positions
 *
 * @see https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/react/
 */

type SearchRequestParams = { query?: string; filters?: string };

const EMPTY_RESULT = {
  hits: [],
  nbHits: 0,
  nbPages: 0,
  page: 0,
  processingTimeMS: 0,
  hitsPerPage: 0,
  exhaustiveNbHits: false,
  query: '',
  params: '',
};

const hasSearchIntent = (params?: SearchRequestParams) => !!params?.query || !!params?.filters;

export const createInstantSearchProxy = <T extends Record<string, unknown>>(client: T): T => ({
  ...client,
  search: (requests: Array<{ params?: SearchRequestParams }>) => {
    // Filter out empty requests
    const meaningfulRequests: typeof requests = [];
    const originalIndices: Array<number> = [];
    requests.forEach((request, index) => {
      if (!hasSearchIntent(request.params)) return;
      meaningfulRequests.push(request);
      originalIndices.push(index);
    });

    // @ts-expect-error -- client.search exists on all Algolia search clients
    return client.search(meaningfulRequests).then((response: { results: Array<unknown> }) => {
      // Rebuild the full results array with stubs for filtered requests
      const results: Array<unknown> = requests.map(() => ({ ...EMPTY_RESULT }));
      originalIndices.forEach((originalIndex, i) => {
        results[originalIndex] = response.results[i];
      });

      return { ...response, results };
    });
  },
});

type SearchClientConfig = {
  appID?: string;
  apiKey?: string;
  options?: ClientOptions;
};

/**
 * Creates an Algolia search client with the empty request proxy applied.
 *
 * This helper simplifies the common pattern of wrapping searchClient() with createInstantSearchProxy().
 * Uses sensible defaults from baseConfig (product_families_api_key) since that's used in 78% of cases.
 *
 * @example Use defaults (product_families_api_key)
 * const client = searchClientWithProxy();
 *
 * @example Override for search_api_key use case
 * const client = searchClientWithProxy({ apiKey: baseConfig.search_api_key });
 */
export const searchClientWithProxy = (config?: SearchClientConfig) => {
  const appID = config?.appID ?? baseConfig.app_id ?? '';
  const apiKey = config?.apiKey ?? baseConfig.product_families_api_key ?? '';
  const options = config?.options ?? ProductClient.getAlgoliaCache(baseConfig.app_id ?? '');

  return createInstantSearchProxy(liteClient(appID, apiKey, options));
};
